import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path
import { useAuth } from '../context/AuthContext'; // Adjust path
import styles from '../styles/CreateCampaignPage.module.css'; // Create this CSS file

const CreateCampaignPage = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // --- Form State ---
    const [clientId, setClientId] = useState('');
    const [clientName, setClientName] = useState(''); // Store name when client is selected
    const [startDate, setStartDate] = useState(''); // YYYY-MM-DD
    const [endDate, setEndDate] = useState(''); // YYYY-MM-DD
    const [description, setDescription] = useState('');
    // Service Counts
    const [serviceCounts, setServiceCounts] = useState({
        design: 0,
        reelContentCreation: 0,
        reelPro: 0,
        video: 0,
        motionGraphics: 0,
        cgi: 0,
        sponsorshipManagement: 0,
        printsCount: 0, // Separate count for prints
    });
    const [printsDetails, setPrintsDetails] = useState('');

    // --- Helper State ---
    const [clients, setClients] = useState([]); // For client dropdown
    const [loadingClients, setLoadingClients] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Fetch clients for dropdown
    useEffect(() => {
        const fetchClients = async () => {
            setLoadingClients(true);
            try {
                const clientsCol = collection(db, 'clients');
                const q = query(clientsCol, orderBy('name', 'asc'));
                const snapshot = await getDocs(q);
                const clientList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name || 'Unnamed Client'
                }));
                setClients(clientList);
            } catch (err) {
                console.error("Error fetching clients:", err);
                setError("Could not load client list.");
            } finally {
                setLoadingClients(false);
            }
        };
        fetchClients();
    }, []);

    // Handle Client Selection Change
    const handleClientChange = (e) => {
        const selectedId = e.target.value;
        const selectedClient = clients.find(c => c.id === selectedId);
        setClientId(selectedId);
        setClientName(selectedClient ? selectedClient.name : '');
    };

    // Handle Service Count Change (Generic handler)
    const handleServiceCountChange = (e) => {
        const { name, value } = e.target;
        const count = parseInt(value, 10);
        // Ensure count is not negative and is a number
        if (!isNaN(count) && count >= 0) {
            setServiceCounts(prevCounts => ({
                ...prevCounts,
                [name]: count
            }));
        } else if (value === '') {
             // Allow clearing the input
             setServiceCounts(prevCounts => ({
                ...prevCounts,
                [name]: 0 // Reset to 0 if cleared
            }));
        }
    };

    // Handle Form Submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // --- Validation ---
        if (!clientId) { setError("Please select a client."); return; }
        if (!startDate) { setError("Please select a start date."); return; }
        if (!endDate) { setError("Please select an end date."); return; }
        if (new Date(startDate) > new Date(endDate)) { setError("End date cannot be before start date."); return; }
        if (!description.trim()) { setError("Please enter a campaign description."); return; }
        if (!currentUser) { setError("You must be logged in to create a campaign."); return; } // Should be caught by route protection too
        // --- End Validation ---

        setSubmitting(true);

        // Construct the services object for Firestore
        const servicesData = {
            design: serviceCounts.design || 0,
            reelContentCreation: serviceCounts.reelContentCreation || 0,
            reelPro: serviceCounts.reelPro || 0,
            video: serviceCounts.video || 0,
            motionGraphics: serviceCounts.motionGraphics || 0,
            cgi: serviceCounts.cgi || 0,
            sponsorshipManagement: serviceCounts.sponsorshipManagement || 0,
            prints: {
              count: serviceCounts.printsCount || 0,
              details: printsDetails
            }
          };

        try {
            const campaignsCol = collection(db, 'campaigns');
            await addDoc(campaignsCol, {
                clientId,
                clientName, // Store selected client name
                startDate: new Date(startDate),
                endDate: new Date(endDate),
                description,
                services: servicesData,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: currentUser.uid,
                status: "Planning" // Initial status
            });
            console.log("Campaign added successfully!");
            navigate('/dashboard'); // Redirect after creation (or to a campaign list page)
        } catch (err) {
            console.error("Error adding campaign: ", err);
            setError('Failed to create campaign. Please check details and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Define service types for mapping
    const serviceTypes = [
        { key: 'design', label: 'Design Assets' },
        { key: 'reelContentCreation', label: 'Reel (Content Creation)' },
        { key: 'reelPro', label: 'Reel (Professional)' },
        { key: 'video', label: 'Video Production' },
        { key: 'motionGraphics', label: 'Motion Graphics' },
        { key: 'cgi', label: 'CGI Elements' },
        { key: 'sponsorshipManagement', label: 'Sponsorship Management' },
    ];

    return (
        <div className={styles.pageContainer}>
            <div className={styles.formBox}>
                <h2 className={styles.formTitle}>Create New Campaign</h2>
                {error && <div className={styles.errorMessage}>{error}</div>}

                <form onSubmit={handleSubmit} className={styles.form}>

                    {/* Client and Dates Section */}
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Basic Information</h3>
                        <div className={styles.gridTwoCols}>
                            <div className={styles.inputGroup}>
                                <label htmlFor="client">Client</label>
                                <select
                                    id="client"
                                    value={clientId}
                                    onChange={handleClientChange}
                                    required
                                    disabled={loadingClients || submitting}
                                >
                                    <option value="" disabled>-- Select a Client --</option>
                                    {loadingClients ? (
                                        <option disabled>Loading clients...</option>
                                    ) : (
                                        clients.map(client => (
                                            <option key={client.id} value={client.id}>{client.name}</option>
                                        ))
                                    )}
                                    {!loadingClients && clients.length === 0 && <option disabled>No clients found</option>}
                                </select>
                            </div>
                             {/* Placeholder or could be combined */}
                            <div></div>

                             <div className={styles.inputGroup}>
                                <label htmlFor="startDate">Start Date</label>
                                <input type="date" id="startDate" value={startDate} onChange={(e) => setStartDate(e.target.value)} required disabled={submitting} />
                            </div>
                             <div className={styles.inputGroup}>
                                <label htmlFor="endDate">End Date</label>
                                <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} required disabled={submitting} />
                            </div>
                        </div>
                         <div className={styles.inputGroup}>
                            <label htmlFor="description">Campaign Description</label>
                            <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} required rows={4} disabled={submitting} />
                        </div>
                    </div>

                    {/* Services Section */}
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Campaign Services &amp; Scope</h3>
                        <p className={styles.sectionSubtitle}>Enter the quantity needed for each service.</p>
                        <div className={styles.servicesGrid}>
                            {/* Map service types to number inputs */}
                            {serviceTypes.map(service => (
                                <div className={styles.inputGroup} key={service.key}>
                                    <label htmlFor={service.key}>{service.label}</label>
                                    <input
                                        type="number"
                                        id={service.key}
                                        name={service.key} // Name matches state key
                                        min="0"
                                        step="1" // Allow only whole numbers
                                        value={serviceCounts[service.key]}
                                        onChange={handleServiceCountChange}
                                        disabled={submitting}
                                        className={styles.countInput}
                                    />
                                </div>
                             ))}
                            {/* Special Handling for Prints */}
                             <div className={`${styles.inputGroup} ${styles.printsGroup}`}>
                                <label htmlFor="printsCount">Prints (Count)</label>
                                <input
                                    type="number"
                                    id="printsCount"
                                    name="printsCount" // Matches state key
                                    min="0"
                                    step="1"
                                    value={serviceCounts.printsCount}
                                    onChange={handleServiceCountChange}
                                    disabled={submitting}
                                    className={styles.countInput}
                                />
                            </div>
                            <div className={`${styles.inputGroup} ${styles.printsDetailsGroup}`}>
                                <label htmlFor="printsDetails">Prints (Details)</label>
                                <textarea
                                    id="printsDetails"
                                    value={printsDetails}
                                    onChange={(e) => setPrintsDetails(e.target.value)}
                                    rows={3}
                                    placeholder="e.g., Brochures (A4, 1000), Posters (A2, 50)"
                                    disabled={submitting || serviceCounts.printsCount === 0} // Disable if count is 0
                                />
                            </div>
                        </div>
                    </div>

                    {/* Submission Buttons */}
                    <div className={styles.buttonGroup}>
                        <button type="button" className={`${styles.formButton} ${styles.cancelButton}`} onClick={() => navigate(-1)} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className={`${styles.formButton} ${styles.submitButton}`} disabled={submitting || loadingClients}>
                            {submitting ? <span className={styles.loadingSpinner}></span> : 'Create Campaign'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateCampaignPage;